// src/components/AskClaude.tsx
// Chat-Komponente: Dani kann Claude direkt Fragen stellen

import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, MessageCircle } from 'lucide-react';
import { callClaude } from '../lib/claude';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function AskClaude() {
  const { courseId } = useParams<{ courseId: string }>();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: question };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const systemPrompt = `Du bist ein freundlicher Spanisch-Tutor für eine Schülerin die sich auf eine Schularbeit vorbereitet.
Der Kurs behandelt Vokabeln aus Unidad 3 — Themen: Schulräume & Orte, Schulsachen, Personen & Berufe, Wochentage & Schulfächer, Verben & Ausdrücke.
Antworte IMMER auf Deutsch. Halte deine Antworten kurz und verständlich.
Wenn die Schülerin nach einer Vokabel fragt, gib die Übersetzung und einen Beispielsatz.
Wenn sie Grammatik-Fragen hat, erkläre es einfach und mit Beispielen.`;

      const apiMessages = [
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: question },
      ];

      const data = await callClaude({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 500,
        system: systemPrompt,
        messages: apiMessages,
      }, 'ask-claude');
      const assistantText = data.content?.[0]?.text || 'Entschuldigung, ich konnte keine Antwort generieren.';
      setMessages(prev => [...prev, { role: 'assistant', content: assistantText }]);
    } catch (error) {
      console.error('Claude API error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler. Bitte versuche es nochmal.',
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div style={{ padding: '20px 16px 120px', maxWidth: '600px', margin: '0 auto' }}>
      {/* Header */}
      <Link to={`/course/${courseId}`} className="back-link" style={{ marginBottom: '16px', display: 'inline-flex' }}>
        <ArrowLeft size={18} />
        Zurück zum Kurs
      </Link>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
      }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #ff5722, #ff9800)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <MessageCircle size={24} color="#fff" />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#ffd54f', fontFamily: '"Fredoka One", cursive' }}>
            Frag Claude
          </h2>
          <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
            Stell deine Fragen zu Spanisch-Vokabeln
          </p>
        </div>
      </div>

      {/* Chat messages */}
      <div style={{
        minHeight: '200px',
        marginBottom: '16px',
      }}>
        {messages.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'rgba(255,255,255,0.4)',
          }}>
            <MessageCircle size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
            <p style={{ fontSize: '15px', margin: 0 }}>
              Frag mich etwas zu den Vokabeln!
            </p>
            <p style={{ fontSize: '13px', margin: '8px 0 0' }}>
              z.B. "Was heißt Turnhalle auf Spanisch?" oder "Erkläre mir die Wochentage"
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: '12px',
          }}>
            <div style={{
              maxWidth: '85%',
              padding: '12px 16px',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #2196f3, #1976d2)'
                : 'rgba(255,255,255,0.08)',
              color: '#fff',
              fontSize: '14px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
            <div style={{
              padding: '12px 16px',
              borderRadius: '16px 16px 16px 4px',
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}>
              <Loader2 size={16} className="loading-spinner" />
              Denke nach...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} style={{
        position: 'fixed',
        bottom: '70px',
        left: '16px',
        right: '16px',
        maxWidth: '568px',
        margin: '0 auto',
        display: 'flex',
        gap: '8px',
        padding: '12px',
        background: '#0f1535',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.1)',
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Deine Frage..."
          rows={1}
          style={{
            flex: 1,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '15px',
            padding: '10px 14px',
            resize: 'none',
            outline: 'none',
            fontFamily: 'Nunito, sans-serif',
          }}
        />
        <button
          type="submit"
          disabled={!input.trim() || isLoading}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            background: input.trim() && !isLoading
              ? 'linear-gradient(135deg, #ff5722, #ff9800)'
              : 'rgba(255,255,255,0.1)',
            border: 'none',
            color: '#fff',
            cursor: input.trim() && !isLoading ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Send size={20} />
        </button>
      </form>
    </div>
  );
}
